import { useState } from 'react';
import { useDrop } from 'react-dnd';
import { useScript, useUpdateScript } from './ScriptContext';
import { getNode } from './treeUtils';
import REGISTRY from '../blockRegistry';
import Block from './Block';
import { ItemTypes } from './dndTypes';

const DEPTH_LIMIT = 5;

function DropGap({ containerPath, index, emptyLabel }) {
  const { compId, comps, clipboard } = useScript();
  const update = useUpdateScript();
  const [hovered, setHovered] = useState(false);

  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.STATEMENT,
    drop: (item, monitor) => {
      if (monitor.didDrop()) return;

      if (item.origin === 'palette') {
        const newNode = JSON.parse(JSON.stringify(REGISTRY[item.blockType].defaultNode));
        update((script) => {
          getNode(script, containerPath).children.splice(index, 0, newNode);
        });
      } else {
        const block = JSON.parse(JSON.stringify(
          getNode(comps[compId].script, item.path)
        ));
        update((script) => {
          const oldParentPath = item.path.slice(0, -1);
          const oldIndex = item.path[item.path.length - 1];
          getNode(script, oldParentPath).children.splice(oldIndex, 1);

          let adjustedIndex = index;
          const sameContainer = pathsEqual(oldParentPath, containerPath);
          if (sameContainer && oldIndex < index) adjustedIndex--;

          getNode(script, containerPath).children.splice(adjustedIndex, 0, block);
        });
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop(),
    }),
  }), [containerPath, index, comps, compId]);

  const active = isOver && canDrop;
  const canPaste = clipboard?.kind === 'statement';
  // Always compute label so the button text is stable during the fade-out animation.
  const pasteLabel = clipboard ? (REGISTRY[clipboard.node?.type]?.shortLabel || 'block') : '';
  const showSplit = hovered && canPaste && !active;

  const handlePaste = (e) => {
    e.stopPropagation();
    if (!canPaste) return;
    const newNode = JSON.parse(JSON.stringify(clipboard.node));
    update((script) => {
      getNode(script, containerPath).children.splice(index, 0, newNode);
    });
  };

  // Shared animated paste half — always in DOM, revealed via CSS transition.
  // maxWidth: 0 → 240px provides the slide-in effect; opacity handles the fade.
  const pasteHalf = (fontSize) => (
    <div style={{
      display: 'flex',
      alignItems: 'stretch',
      maxWidth: showSplit ? '240px' : '0',
      opacity: showSplit ? 1 : 0,
      overflow: 'hidden',
      flexShrink: 0,
      transition: 'max-width 0.2s ease, opacity 0.16s ease',
      pointerEvents: showSplit ? 'auto' : 'none',
    }}>
      <div style={{ width: '1px', backgroundColor: 'rgba(0,0,0,0.1)', flexShrink: 0 }} />
      <button
        onClick={handlePaste}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 14px',
          border: 'none',
          borderRadius: '0 3px 3px 0',
          cursor: 'pointer',
          fontFamily: 'Work Sans, sans-serif',
          fontSize,
          backgroundColor: 'transparent',
          color: '#6b7280',
          whiteSpace: 'nowrap',
          transition: 'background 0.1s, color 0.1s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(37,99,235,0.07)'; e.currentTarget.style.color = '#2563eb'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#6b7280'; }}
      >
        ⊕ Paste: {pasteLabel}
      </button>
    </div>
  );

  // ── Empty-slot mode ──────────────────────────────────────────────────────────
  if (emptyLabel) {
    return (
      <div
        ref={drop}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          fontSize: '0.75vmax',
          fontFamily: 'Work Sans, sans-serif',
          userSelect: 'none',
          border: `1.5px dashed ${active ? '#2563eb' : hovered ? 'rgba(37,99,235,0.35)' : 'rgba(0,0,0,0.15)'}`,
          borderRadius: '4px',
          margin: '4px 0',
          backgroundColor: active ? 'rgba(37,99,235,0.06)' : hovered ? 'rgba(37,99,235,0.03)' : 'transparent',
          transition: 'border-color 0.18s ease, background-color 0.18s ease',
          minHeight: '36px',
          display: 'flex',
          alignItems: 'stretch',
          overflow: 'hidden',
        }}
      >
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '10px 12px',
          color: active ? '#2563eb' : '#9ca3af',
          transition: 'color 0.15s ease',
        }}>
          {active ? 'drop here' : emptyLabel}
        </div>
        {pasteHalf('0.75vmax')}
      </div>
    );
  }

  // ── Thin-gap mode (between existing blocks) ──────────────────────────────────
  // A plain hover (no drag, no clipboard) must still expand the gap so the
  // add/paste affordances are discoverable — otherwise the gap is an invisible
  // 6px sliver and there is no visible place to insert a block.
  const expanded = active || showSplit || hovered;

  return (
    <div
      ref={drop}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        // Resting state keeps a small hit area so the gap is hoverable.
        height: expanded ? '36px' : '12px',
        margin: '2px 0',
        borderRadius: '4px',
        backgroundColor: active ? 'rgba(37,99,235,0.08)' : expanded ? 'rgba(37,99,235,0.03)' : 'transparent',
        border: active
          ? '1.5px dashed #2563eb'
          : expanded
            ? '1.5px dashed rgba(37,99,235,0.3)'
            : '1.5px dashed transparent',
        transition: 'height 0.15s ease, border-color 0.18s ease, background-color 0.18s ease',
        display: 'flex',
        alignItems: 'stretch',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Resting hint — a faint dashed insertion line, always visible so the
          drop point can be found, fading out as the gap expands. */}
      <div style={{
        position: 'absolute',
        left: '8px',
        right: '8px',
        top: '50%',
        transform: 'translateY(-50%)',
        borderTop: '1.5px dashed rgba(0,0,0,0.13)',
        opacity: expanded ? 0 : 1,
        transition: 'opacity 0.15s ease',
        pointerEvents: 'none',
      }} />

      {/* Left label — "drop here" — fades in whenever the gap is expanded */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.7vmax',
        fontFamily: 'Work Sans, sans-serif',
        userSelect: 'none',
        color: active ? '#2563eb' : '#9ca3af',
        opacity: expanded ? 1 : 0,
        transition: 'opacity 0.15s ease, color 0.15s ease',
      }}>
        {active ? 'drop here' : '+ drop a block here'}
      </div>
      {pasteHalf('0.7vmax')}
    </div>
  );
}

export default function StatementList({ containerPath, emptyLabel = 'drag blocks here', depth = 0, breadcrumb = [] }) {
  const { compId, comps, pushDrill } = useScript();

  let blocks = [];
  try {
    blocks = getNode(comps[compId].script, containerPath).children || [];
  } catch (e) {
    blocks = [];
  }

  const typedBlocks = blocks.filter((b) => b && b.type);

  // ── Deep-nesting stub ────────────────────────────────────────────────────────
  if (depth >= DEPTH_LIMIT) {
    const count = typedBlocks.length;
    return (
      <div
        onClick={() => pushDrill({ containerPath, breadcrumb })}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#2563eb';
          e.currentTarget.style.color = '#2563eb';
          e.currentTarget.style.backgroundColor = 'rgba(37,99,235,0.04)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#d1d5db';
          e.currentTarget.style.color = '#6b7280';
          e.currentTarget.style.backgroundColor = '#f9fafb';
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '6px 12px',
          margin: '4px 0',
          border: '1.5px dashed #d1d5db',
          borderRadius: '4px',
          backgroundColor: '#f9fafb',
          cursor: 'pointer',
          fontFamily: 'Work Sans, sans-serif',
          fontSize: '0.72vmax',
          color: '#6b7280',
          userSelect: 'none',
          transition: 'border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease',
        }}
      >
        <span>{count > 0 ? `${count} block${count !== 1 ? 's' : ''} inside` : 'empty · click to open'}</span>
        <span style={{ fontSize: '0.68vmax', opacity: 0.65 }}>Open ↗</span>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '40px' }}>
      {typedBlocks.length === 0 ? (
        <DropGap containerPath={containerPath} index={0} emptyLabel={emptyLabel} />
      ) : (
        <>
          <DropGap containerPath={containerPath} index={0} />
          {typedBlocks.map((node) => {
            const actualIndex = blocks.indexOf(node);
            const blockPath = [...containerPath, actualIndex];
            return (
              <div key={`${blockPath.join('-')}-${node.type}`}>
                <Block path={blockPath} depth={depth} breadcrumb={breadcrumb} />
                <DropGap containerPath={containerPath} index={actualIndex + 1} />
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

function pathsEqual(a, b) {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}
